<template>
  <SlideOver :open="on" @close="close">
    <div class="w-screen max-w-md h-full">
      <form
        @submit.prevent="storeEquipmentCalibration"
        class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
      >
        <div class="flex-1 h-0 overflow-y-auto">
          <div class="py-6 px-4 bg-light-blue-700 sm:px-6">
            <div class="flex items-center justify-between">
              <h2
                id="slide-over-heading"
                class="text-lg font-medium text-white"
              >
                {{ t("equipment_calibration_create") }}
              </h2>
              <div class="ml-3 h-7 flex items-center">
                <button
                  type="button"
                  @click="close()"
                  class="
                    bg-light-blue-700
                    rounded-md
                    focus:outline-none focus:ring-2 focus:ring-white
                  "
                >
                  <XIcon class="h-6 w-6 hover:text-white text-light-blue-200" />
                </button>
              </div>
            </div>
            <div class="mt-1">
              <p class="text-sm text-light-blue-300">
                {{ t("description") }}
              </p>
            </div>
          </div>
          <div class="flex-1 flex flex-col justify-between">
            <div class="px-4 divide-y divide-gray-200 sm:px-6">
              <div class="space-y-6 pt-6 pb-5">
                <ui-input
                  ref="calibrated_on"
                  :invalid="'calibrated_on' in form.errors"
                  :error="form.errors.calibrated_on"
                  type="date"
                  :label="t('calibrated_on')"
                  v-model="form.calibrated_on"
                ></ui-input>

                <ui-textarea
                  :invalid="'comments' in form.errors"
                  :error="form.errors.comments"
                  type="text"
                  :label="t('comments')"
                  v-model="form.comments"
                ></ui-textarea>

                <!-- TODO: Refactor to input component -->
                <div
                  class="
                    flex
                    justify-center
                    px-6
                    pt-5
                    pb-6
                    border-2 border-gray-300 border-dashed
                    rounded-md
                  "
                  :class="{
                    'border-red-600 hover:border-red-600':
                      'calibration_file' in form.errors,
                  }"
                >
                  <div class="space-y-1 text-center">
                    <svg
                      class="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </svg>
                    <div class="flex justify-center text-sm text-gray-600">
                      <label
                        for="file-upload"
                        class="
                          flex flex-col
                          justify-center
                          text-center
                          relative
                          cursor-pointer
                          bg-white
                          rounded-md
                          font-medium
                          text-light-blue-600
                          hover:text-light-blue-500
                          focus-within:outline-none
                          focus-within:ring-2
                          focus-within:ring-offset-2
                          focus-within:ring-light-blue-500
                        "
                      >
                        <p class="w-full" v-if="form.calibration_file">
                          {{ form.calibration_file.name }}
                        </p>
                        <p class="">
                          {{ t("upload_file") }}
                        </p>
                        <input
                          @input="
                            form.calibration_file = $event.target.files[0]
                          "
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          class="sr-only"
                        />
                      </label>
                    </div>
                    <p class="text-xs text-gray-500">
                      {{ t("file_types") }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex space-x-1 m-4"
                  v-if="'calibration_file' in form.errors"
                >
                  <ExclamationCircleIcon class="h-5 w-5 text-red-500" />
                  <p class="text-red-600 text-sm">
                    {{ form.errors.calibration_file }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 px-4 py-4 flex justify-end">
          <ui-button
            backgroundStyling="bg-white"
            borderStyling="border border-gray-300"
            textColorStyling="text-gray-700 hover:text-gray-500"
            type="button"
            @click="close"
            >{{ t("cancel") }}</ui-button
          >

          <ui-button :disabled="form.processing" type="submit" class="ml-4">
            {{ t("save") }}
          </ui-button>
        </div>
      </form>
    </div>
  </SlideOver>
</template>

<script>
import { XIcon, ExclamationCircleIcon } from "@heroicons/vue/outline";
import { useI18n } from "vue-i18n";
import SlideOver from "@/Shared/SlideOver";

export default {
  emits: ["toggleVisibility"],

  components: {
    XIcon,
    SlideOver,
    ExclamationCircleIcon,
  },

  props: {
    on: { type: Boolean, required: true },

    equipment: { type: Object, required: true },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  mounted() {
    if (this.on) {
      setTimeout(() => {
        this.$refs.calibrated_on.$refs.input.focus();
      }, 250);
    }
  },

  data() {
    return {
      form: this.$inertia.form({
        _method: "POST",
        comments: "",
        calibrated_on: "",
        calibration_file: "",
      }),
    };
  },

  methods: {
    storeEquipmentCalibration() {
      this.form.post(
        this.route("equipment.equipment-calibrations.store", {
          equipment: this.equipment.data.id,
          search: this.$page.props.filters.search,
        }),
        {
          preserveScroll: true,
          onSuccess: (page) => {
            this.close();
          },
        }
      );
    },

    close() {
      this.form.reset();
      this.form.clearErrors();
      this.$emit("toggleVisibility");
    },
  },

  watch: {
    on: {
      handler: (on) => {
        if (on) {
          setTimeout(() => {
            this.$refs.calibrated_on.$refs.input.focus();
          }, 250);
        }
      },
    },
  },
};
</script>

<i18n>
{
  "es": {
    "equipment_calibration_create": "Agregar calibración",
    "description": "Establece la información de la calibración",
    "calibrated_on": "Fecha de calibración",
    "comments": "Comentarios",
    "save": "Guardar",
    "cancel": "Cancelar",
    "upload_file": "Cargar Archivo",
    "file_types": "PNG o JPG hasta 10MB"
  }
}
</i18n>
